import { createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import * as routerPkg from "@tanstack/solid-router";
import { type AccountsState, getActiveAccount, type StoredAccount } from "@bodega/shared";
import { ACCOUNTS_CHANGED_EVENT, accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { resolveTabAccount } from "@/lib/active-account";
import {
  activeTabAccountId,
  clearTabAccountId,
  TAB_ACCOUNT_CHANGED_EVENT,
  writeTabAccountId,
} from "@/lib/tab-account";
import { pruneAliases } from "@/lib/account-alias";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const routerPkgObj = routerPkg as {
    useRouter?: (opts?: { warn?: boolean }) => { state?: { matches?: { fullPath?: string }[] } };
  };
  const router =
    "useRouter" in routerPkgObj && typeof routerPkgObj.useRouter === "function"
      ? routerPkgObj.useRouter({ warn: false })
      : undefined;
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(readAccountsStateSync());

  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();
    const onStorage = (e: StorageEvent) => {
      if (e.key === "bodega_accounts" || e.key === null) {
        reload();
      }
    };
    const onLocalChange = () => {
      reload();
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    });
  });

  const accounts = () => state().accounts;

  const active = (): StoredAccount | null => resolveTabAccount(state(), activeTabAccountId());
  const activeUserId = () => active()?.userId ?? null;

  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    if (!next.accounts.some((a) => a.userId === userId)) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);

    // D1 & D2: Preserve section route; fall back to section if on entity route ($)
    const matches = router?.state?.matches;
    const matchWithParam = matches?.findLast((m: { fullPath?: string }) =>
      Boolean(m.fullPath && m.fullPath.includes("$")),
    );
    if (matchWithParam?.fullPath) {
      const dollarIdx = matchWithParam.fullPath.indexOf("$");
      const prefix = matchWithParam.fullPath.slice(0, dollarIdx).replace(/\/+$/, "");
      const target = prefix.length > 0 ? prefix : "/";
      await (navigate as (opts: { to: string }) => Promise<void>)({ to: target });
    }
  };

  const switchHere = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    if (!next.accounts.some((a) => a.userId === userId)) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);
  };

  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    if (!guest) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(guest);
    writeTabAccountId(guest.userId);
    // D5: Continuing as guest crosses a privilege boundary; safe landing at root is deliberate
    await navigate({ to: "/" });
  };

  const removeAccount = async (account: StoredAccount) => {
    const wasActive = activeTabAccountId() === account.userId;
    const next = await accountsStore.remove(account.userId);
    setState(next);
    pruneAliases(next.accounts.map((a) => a.userId));
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        writeTabAccountId(fallback.userId);
        await navigate({ to: "/" });
      } else {
        clearTabAccountId();
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    switchTo,
    switchHere,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
